<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { useMediaQuery } from "@vueuse/core";
import { computed, onUnmounted, ref, watch } from "vue";
import "vue-sonner/style.css";
import { isProblem, onUnauthorized, setCsrfToken } from "@/api/client";
import { startLiveEvents } from "@/api/events";
import { useMe } from "@/api/me";
import AppShell from "@/components/shell/AppShell.vue";
import SessionExpiredDialog from "@/components/shell/SessionExpiredDialog.vue";
import SignInScreen from "@/components/shell/SignInScreen.vue";
import SplashScreen from "@/components/shell/SplashScreen.vue";
import ConfirmHost from "@/components/common/ConfirmHost.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useTheme } from "@/composables/useTheme";

useTheme();
const queryClient = useQueryClient();
const me = useMe();
const expired = ref(false);

watch(
  () => me.data.value,
  (value) => {
    setCsrfToken(value?.csrfToken ?? null);
    if (value) {
      expired.value = false;
      startLiveEvents(queryClient);
    }
  },
  { immediate: true },
);

const stop = onUnauthorized(() => {
  if (me.data.value) expired.value = true;
});
onUnmounted(stop);

// Below lg the bottom tab bar (AppShell) owns the bottom 64px: toasts sit above it.
const tabBar = useMediaQuery("(max-width: 1023.98px)");
const toastOffset = computed(() => (tabBar.value ? { bottom: "calc(5rem + env(safe-area-inset-bottom))" } : undefined));

const state = computed(() => {
  if (me.data.value) return "ready";
  if (me.isPending.value) return "loading";
  if (isProblem(me.error.value) && me.error.value.status === 401) return "signed-out";
  return "error";
});
</script>

<template>
  <TooltipProvider :delay-duration="300">
    <SplashScreen v-if="state === 'loading'" />
    <SignInScreen v-else-if="state === 'signed-out'" />
    <div v-else-if="state === 'error'" class="grid min-h-dvh place-items-center p-6">
      <ErrorState :error="me.error.value" class="max-w-md" @retry="me.refetch()" />
    </div>
    <AppShell v-else />
    <SessionExpiredDialog :open="expired" />
    <ConfirmHost />
    <Toaster rich-colors close-button position="bottom-right" :offset="toastOffset" :mobile-offset="toastOffset" />
  </TooltipProvider>
</template>
